'use client'

import { type JSX, use, useEffect, useState } from 'react'

import { HaloPulse } from './hero-halo'
import useReducedMotion from './use-reduced-motion'

// `setupPlatformRotation` (`app.js:776-842`), values unchanged: each name holds, then the next one is
// typed out a character at a time at a slightly uneven pace, and the halo pulses as it completes.
const WORDS = ['ChatGPT', 'Gemini', 'Claude']
const TIME_SCALE = 2
const FIRST_HOLD = 3000 * TIME_SCALE
const HOLD = 2600
const typingDelay = (typed: number): number => (42 + ((typed * 17) % 34)) * TIME_SCALE

/**
 * The platform name in the hero headline, with its blinking caret. The text is state; one timeout is
 * pending at a time, cleared on every step and on unmount. A timeout rather than an interval because
 * the typing delay changes per character. Not CSS: a typewriter in CSS needs a monospace font.
 *
 * Under reduced motion it shows "ChatGPT" and no caret, and schedules nothing. The changing text is
 * not meant to be announced: the headline holding it carries the full sentence as its accessible
 * name and hides its visual spans, as on production.
 *
 * Colour: `accent`, in both themes. The caret is the headline's ink.
 */
export default function PlatformRotator(): JSX.Element {
  const [{ word, typed, hold }, setStep] = useState({ word: 0, typed: WORDS[0].length, hold: FIRST_HOLD })
  const reducedMotion = useReducedMotion()
  const pulse = use(HaloPulse)
  const text = WORDS[word]
  const streaming = typed < text.length

  useEffect(() => {
    if (reducedMotion) return undefined

    const timer = window.setTimeout(
      () => {
        if (!streaming) {
          setStep({ word: (word + 1) % WORDS.length, typed: 0, hold: HOLD })
          return
        }
        if (typed + 1 === text.length) pulse()
        setStep({ word, typed: typed + 1, hold })
      },
      streaming ? typingDelay(typed) : hold,
    )

    return (): void => window.clearTimeout(timer)
  }, [reducedMotion, word, typed, hold, streaming, text, pulse])

  return (
    <span className="inline-flex min-w-[7.15ch] items-baseline">
      <span className="text-accent">{reducedMotion ? WORDS[0] : text.slice(0, typed)}</span>
      <span
        data-streaming={streaming || undefined}
        className="ml-[0.075em] inline-block h-[0.74em] w-[0.045em] flex-none translate-y-[0.035em] animate-caret bg-current data-streaming:[animation-duration:0.58s] motion-reduce:hidden"
      />
    </span>
  )
}
